import { Provider } from 'react-redux';
import { BrowserRouter, Route, Routes } from 'react-router';

import { config } from './config';
import { handleLoginRedirect, main, store } from './configuration';
import { AppContainer } from './features/AppContainer';
import { ErrorBoundary } from './features/app/common/ErrorBoundary';
import { NoMatch } from './features/app/common/NoMatch';
import {
    MARKETPLACE_FREE_TRIAL_BUTTON,
    MARKETPLACE_FREE_TRIAL_SECTION_FOR_HOMESCREEN,
    MARKETPLACE_START_FREE_TRIAL,
} from './features/app/common/routes';
import { FreeTrialButtonWrapper } from './features/app/freeTrial/FreeTrialButtonWrapper';
import { FreeTrialPopupContainer } from './features/app/freeTrial/FreeTrialPopupContainer';
import { HomescreenFreeTrialSection } from './features/app/freeTrial/HomescreenFreeTrialSection';
import { IntlWrapper } from './IntlWrapper';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import type { IConfigCatLogger } from '@configcat/sdk';
import { ConfigCatProvider, LogLevel, PollingMode } from 'configcat-react';
import { createRoot } from 'react-dom/client';

import { reportErrorToSentry } from './configuration/setup/sentry';

function renderApplication() {
    const container = document.getElementById('root');
    // biome-ignore lint/style/noNonNullAssertion: Container is always defined in this case
    const root = createRoot(container!);

    const sentryLogger: IConfigCatLogger = {
        level: LogLevel.Error,
        log(level, eventId, message, exception) {
            if (level === LogLevel.Error) {
                reportErrorToSentry(exception ?? String(message), { extra: { eventId, message: String(message) } });
            }
        },
    };

    const configCatOptions = {
        pollIntervalSeconds: 60,
        maxInitWaitTimeSeconds: 5,
        logger: sentryLogger,
    };

    root.render(
        <ErrorBoundary>
            <Provider store={store}>
                <IntlWrapper>
                    <ConfigCatProvider
                        sdkKey={config.featureToggles.configCatSdkKey}
                        pollingMode={PollingMode.AutoPoll}
                        options={{ ...configCatOptions, cacheTimeToLiveSeconds: 60 }}
                    >
                        <BrowserRouter>
                            <Routes>
                                <Route path='/error' element={<NoMatch />} />
                                <Route path={MARKETPLACE_FREE_TRIAL_BUTTON} element={<FreeTrialButtonWrapper />} />
                                <Route
                                    path={MARKETPLACE_FREE_TRIAL_SECTION_FOR_HOMESCREEN}
                                    element={<HomescreenFreeTrialSection />}
                                />
                                <Route path={MARKETPLACE_START_FREE_TRIAL} element={<FreeTrialPopupContainer />} />
                                <Route path='/*' element={<AppContainer />} />
                                <Route element={<NoMatch />} />
                            </Routes>
                        </BrowserRouter>
                    </ConfigCatProvider>
                </IntlWrapper>
            </Provider>
        </ErrorBoundary>
    );
}

if (window.location.href.startsWith(config.login.redirectUri)) {
    handleLoginRedirect();
} else {
    main(renderApplication);
}
